import type { CalculateControllerFunction } from 'pages/layout/TwoColumn'
import { TwoColumnAnimatedPane } from 'pages/layout/TwoColumn'
import { StudentAssignmentPage } from '../assignment/StudentAssignmentPage'
import { StudentLibraryPage } from './StudentLibraryPage'
import { useImpersonatedBreakoutUser } from 'hooks/profile'
import { useRepository } from 'hooks/auth'
import { useLTIMode } from 'hooks/use-lti-mode'
import { StudentLibraryCubit } from '@breakoutlearning/firebase-repository/cubits/StudentLibraryCubit'
import { useCubitBuilder } from 'hooks/cubits'
import { assignmentPaneKey } from './assignmentPaneKey'

const buildController = (studentLibraryCubit: StudentLibraryCubit) => {
  const calculateAnimationController: CalculateControllerFunction = (
    params,
    route
  ) => {
    const routeTitle = route?.title
    let key: string | null = null
    let component: JSX.Element | null = null
    let mode = 'leave'

    const roomStateId =
      params?.roomStateId && typeof params.roomStateId === 'string'
        ? params.roomStateId
        : undefined
    const assignmentId =
      params?.assignmentId && typeof params.assignmentId === 'string'
        ? params.assignmentId
        : null
    const sectionId =
      params?.sectionId && typeof params.sectionId === 'string'
        ? params.sectionId
        : null

    if (
      (routeTitle === 'assignment' || routeTitle === 'assignmentWithRoom') &&
      assignmentId &&
      sectionId
    ) {
      // Re-mount only when the assignment changes (see assignmentPaneKey).
      key = assignmentPaneKey(assignmentId, sectionId)
      component = (
        <StudentAssignmentPage
          assignmentId={assignmentId}
          sectionId={sectionId}
          roomStateId={roomStateId}
        />
      )
      mode = 'slideIn'
    } else {
      key = 'home'
      component = <StudentLibraryPage cubit={studentLibraryCubit} />
      mode = 'slideOut'
    }

    return {
      key,
      component,
      mode,
    }
  }

  return calculateAnimationController
}

export const StudentLibrary = () => {
  const repository = useRepository()
  const impersonatedBreakoutUser = useImpersonatedBreakoutUser()
  const { ltiOptions } = useLTIMode()
  const cubit = useCubitBuilder(
    () => {
      const sectionId = ltiOptions?.sectionId

      return new StudentLibraryCubit(repository, impersonatedBreakoutUser, {
        sectionId: sectionId?.toString(),
      })
    },
    [repository, ltiOptions, impersonatedBreakoutUser],
    { preventInitialize: true }
  )

  return <TwoColumnAnimatedPane calculateController={buildController(cubit)} />
}
